import { useQueries } from "@tanstack/react-query";
import type { V2Coin } from "../../redacted/v2/action";
import { deploymentConfig, isV2Enabled } from "../../redacted/v2/config";
import { privateFeeMetadata, type FeeAssetMetadata } from "./privateFeeAmounts";
import { readPrivateFeeMetadata } from "./privateFeeMetadata";

/** Balances omit assets locked in products, including staking receipts. Resolve
 * missing fee denominations centrally so product buttons need no special cases.
 * Metadata requests never re-simulate, invalidate or modify the reviewed fee. */
export const usePrivateFeeMetadata = (
  coins: readonly V2Coin[],
  assets: readonly FeeAssetMetadata[]
): readonly FeeAssetMetadata[] => {
  const missing = [...new Set(coins.map((coin) => coin.denom))]
    .filter((denom) => !privateFeeMetadata(denom, assets))
    .sort();
  const lcdUrl =
    missing.length && isV2Enabled() ? deploymentConfig().lcdUrl : undefined;
  const results = useQueries({
    queries: lcdUrl
      ? missing.map((denom) => ({
          queryKey: ["private-fee-metadata", lcdUrl, denom],
          queryFn: () => readPrivateFeeMetadata(lcdUrl, denom),
          staleTime: 30 * 60_000,
          gcTime: 60 * 60_000,
          retry: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          refetchInterval: false as const,
        }))
      : [],
  });
  return [
    // An invalid preloaded entry must not shadow its resolved replacement.
    ...results.flatMap((result) => (result.data ? [result.data] : [])),
    ...assets,
  ];
};
